import {getEventValueAndLabel} from 'common/report/util/events';
import {findKeyByValue} from 'common/util/typescript';
import {
  ALL_MATH_TYPES,
  DATASET_MIXPANEL,
  FUNNELS_MATH_TYPES,
  PER_USER_AGGREGATIONS,
  PRIMITIVE_MATH_TYPES,
  ResourceType,
  RETENTION_MATH_TYPES,
} from 'common/report/constants';
import {TOP_PEOPLE} from 'common/query-builder/query-block/properties-menu/constants';
import {
  CohortItem,
  FiltersOperator,
  isEventEntry,
  isFormulaEntry,
  isInlineCohortItem,
  isMetricEntry,
  isSavedCohortItem,
  MetricEntry,
  MetricEntryBehavior,
  MetricEntryMeasurement,
  QueryFormulaEntry,
  QueryMetricEntry,
} from 'common/query-builder/query-entry-section/types';
import {BehaviorShowClause, ShowClause} from 'common/report/insights/models/show-clause';
import {FilterClause} from 'common/report/insights/models/filter-clause';
import {ShowSection} from 'common/report/insights/models/section';
import {formatSource} from 'common/report/insights/util';
import {isProperty} from 'common/query-builder/query-block/properties-menu/types';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {ConversionWindowUnit, FunnelOrder} from 'common/report/funnels/models/constants';
import {BirthIntervalUnit, RetentionAlignmentType, RetentionType} from 'common/report/retention/util/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SectionItem} from 'common/components/items-menu/types';
import {getRetentionBucketName} from 'common/report/retention/util';
import {MetricType} from 'common/query-builder/query-block/constants';

import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from '../models/constants';
import {isFormulaShowClause} from '../models/utils';
import {ReportSectionsMetadata, ShowClause as ShowClauseType} from '../util/types';
import {DETERMINER_MAPPING} from './constants';
import {BehaviorMetric, InsightsQueryError, InsightsResourceType} from './types';

/** operators in the legacy per-user format */
const LEGACY_PER_USER_OPERATORS = [
  ALL_MATH_TYPES.AVERAGE,
  ALL_MATH_TYPES.MEDIAN,
  ALL_MATH_TYPES.MAX,
  ALL_MATH_TYPES.MIN,
  ALL_MATH_TYPES.P90,
  ALL_MATH_TYPES.P75,
  ALL_MATH_TYPES.P25,
];

function getDataGroupLabel(dataGroupId, dataGroups = []) {
  const group = dataGroups.find((item) => item.groupID === dataGroupId);

  return group ? formatSource(group.displayName, {all: true, pluralizeDataGroup: true}) : TOP_PEOPLE.label;
}

/**
 * Transforms show clauses to event entries
 */
export function getEntries({
  showClauses,
  dataGroups = [],
  queryError,
  dateUnit,
  reportSectionsMetadata,
}: {
  showClauses: ShowClauseType[];
  dataGroups?: DataGroup[];
  queryError?: InsightsQueryError;
  dateUnit?: TimeUnit;
  reportSectionsMetadata?: ReportSectionsMetadata;
}): Array<QueryMetricEntry | MetricEntry> {
  // TODO opslead
  const entries = [];
  let formulaIdx = 0;
  const formulaErrorByIdx = new Map<number, string>(
    (queryError?.formula_errors || []).map(({index, message}) => [index, message]),
  );
  // eslint-disable-next-line sonarjs/cognitive-complexity
  showClauses.forEach((clause: ShowClauseType, index: number) => {
    if (isFormulaShowClause(clause)) {
      const {name, definition} = clause.formula;
      const error = formulaErrorByIdx.get(formulaIdx);
      const entry: QueryFormulaEntry = {
        type: ResourceType.Formula,
        event: {
          label: name,
          value: definition,
          ...(error && {error}),
        },
        filters: [],
      };
      entries.push(entry);
      formulaIdx += 1;
      return;
    }

    const {behavior, measurement} = clause;
    if (behavior.type === MetricType.Funnel) {
      const metricBehavior: MetricEntryBehavior = {
        // Common behavior fields
        type: behavior.type,
        dataGroupId: behavior.dataGroupId,
        name: getMetricName(behavior),
        renamed: ``,
        behaviors: getMetricEntryBehaviors(behavior), // for inline-custom-event, inline-funnel, inline-retention
        // Funnels specific fields
        funnelOrder: behavior.funnelOrder || FunnelOrder.Loose,
        exclusions: behavior.exclusions || [],
        aggregateBy: behavior.aggregateBy || [],
        conversionWindowDuration: behavior.conversionWindowDuration || 30,
        conversionWindowUnit: behavior.conversionWindowUnit || ConversionWindowUnit.Day,
      };
      const metricMeasurement: MetricEntryMeasurement = {
        // TODO: convert property type properly
        property: (measurement.property as unknown) as PropertySectionItem,
        aggregationOperator: measurement.math,
        cumulative: Boolean(measurement.cumulative),
        // funnel step
        stepIndex: measurement.stepIndex,
      };
      const metricEntry: MetricEntry = {
        type: `metric-entry`,
        behavior: metricBehavior,
        measurement: metricMeasurement,
      };

      entries.push(metricEntry);
      return;
    }
    if (behavior.type === MetricType.Retention) {
      const metricBehavior: MetricEntryBehavior = {
        // Common behavior fields
        type: behavior.type,
        dataGroupId: behavior.dataGroupId,
        name: getMetricName(behavior),
        renamed: ``,
        behaviors: getMetricEntryBehaviors(behavior), // for inline-custom-event, inline-funnel, inline-retention
        // Retention specific fields
        retentionType: behavior.retentionType || `birth`,
        retentionAlignmentType: behavior.retentionAlignmentType || RetentionAlignmentType.Birth,
        retentionUnit: behavior.retentionUnit || TimeUnit.Day,
        retentionUnbounded: behavior.retentionUnbounded,
        retentionCustomBucketSizes: behavior.retentionCustomBucketSizes || [],
      };
      const metricMeasurement: MetricEntryMeasurement = {
        // TODO: convert property type properly
        property: (measurement.property as unknown) as PropertySectionItem,
        aggregationOperator: measurement.math,
        cumulative: Boolean(measurement.cumulative),
        retentionBucketIndex: measurement.retentionBucketIndex,
        retentionBuckets: generateRetentionBuckets({
          showIndex: index,
          retentionBucketIndex: measurement.retentionBucketIndex,
          retentionType: behavior.retentionType,
          retentionUnit: behavior.retentionUnit,
          dateUnit,
          retentionCustomBucketSizes: behavior.retentionCustomBucketSizes,
          reportSectionsMetadata,
        }),
      };
      const metricEntry: MetricEntry = {
        type: `metric-entry`,
        behavior: metricBehavior,
        measurement: metricMeasurement,
      };

      entries.push(metricEntry);
      return;
    }

    const type = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, behavior.resourceType) || ResourceType.Event;

    const props = {
      type: null,
      aggregationOperator: measurement.math,
      aggregationOperatorPerUser: measurement.perUserAggregation,
      dataGroupId: behavior.dataGroupId,
      property: null,
      event: behavior.value as QueryMetricEntry['event'],
      filters: behavior.filter
        ? behavior.filter.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
        : [],
      filtersOperator: behavior.filter?.length
        ? findKeyByValue(DETERMINER_MAPPING, behavior.filterDeterminer)
        : FiltersOperator.And,
      readOnlyEvent: type === ResourceType.User,
      ...(measurement.math === ALL_MATH_TYPES.HISTOGRAM ? {customBucket: behavior.customBucket} : {}),
      ...(measurement.multiAttribution ? {multiAttribution: measurement.multiAttribution} : {}),
      ...(measurement.rolling ? {rolling: measurement.rolling} : {}),
      ...(measurement.cumulative ? {cumulative: measurement.cumulative} : {}),
    };

    const {value, dataGroupId} = behavior;
    switch (type) {
      case ResourceType.Cohort: {
        const inlineCohort = value.raw_cohort;
        const cohortItem: CohortItem = inlineCohort
          ? {
              resourceType: ResourceType.Cohort,
              label: value.name,
              value: {
                cohort: value.raw_cohort
                  ? {
                      raw_cohort: value.raw_cohort,
                      name: value.name,
                    }
                  : value.cohort, // value.cohort should not be used anymore,
              },
            }
          : {
              resourceType: ResourceType.Cohort,
              label: value.name,
              value: value.id,
              ...(value.renamed ? {renamed: value.renamed} : {}),
              ...(dataGroupId ? {data_group_id: dataGroupId} : {}),
            };
        props.type = ResourceType.Cohort;
        props.event = cohortItem;
        break;
      }
      case ResourceType.User:
      case ResourceType.Event: {
        let {value: eventValue, label} = getEventValueAndLabel(value);
        // get user type dataGroupId label
        if (type === ResourceType.User) {
          if (dataGroupId) {
            label = getDataGroupLabel(dataGroupId, dataGroups || []);
          } else {
            label = TOP_PEOPLE.label;
          }
        }

        props.type = type;
        props.event = {
          custom: value.custom || false,
          label: eventValue ? label : `Select Event`,
          value: eventValue,
          ...(value.renamed ? {renamed: value.renamed} : {}),
          ...(value.id ? {id: value.id} : {}),
        };
        break;
      }
    }

    if (measurement.property) {
      const {
        listItemGroup,
        customProperty,
        customPropertyId,
        dataGroupId,
        tempDataGroupId,
        defaultType,
        joinPropertyType,
        propertyObjectKey,
        name,
        resourceType: insightsResourceType,
      } = measurement.property;
      const resourceType = findKeyByValue(QUERY_BUILDER_RESOURCE_TYPE_MAPPING, insightsResourceType);
      props.property = {
        resourceType,
        dataGroupId: dataGroupId ?? behavior.dataGroupId,
        tempDataGroupId: tempDataGroupId ?? null,
        propertyDefaultType: defaultType,
        type: measurement.property.type,
        unit: measurement.property.unit ?? null,
        value: name,
        ...(propertyObjectKey ? {propertyObjectKey} : {}),
        ...(customProperty ? {customProperty} : {}),
        ...(customPropertyId ? {customPropertyId} : {}),
        ...(joinPropertyType ? {joinPropertyType} : {}),
        ...(listItemGroup ? {listItemGroup} : {}),
      };
    }

    // migrate old aggregation format
    if (
      !measurement.property &&
      // this is not in the type def, so doing some hack
      LEGACY_PER_USER_OPERATORS.includes(props.aggregationOperator as typeof LEGACY_PER_USER_OPERATORS[number])
    ) {
      props.aggregationOperatorPerUser = PER_USER_AGGREGATIONS.TOTAL;
    }

    entries.push(props);
  });

  return entries;
}

function getMetricName(behavior: BehaviorMetric['behavior']): string {
  const {type, resourceType} = behavior;
  if (type === MetricType.Funnel) {
    return ``;
  }
  if (type === MetricType.Retention) {
    return ``;
  }
  if (type === MetricType.Event) {
    if (resourceType === InsightsResourceType.People) {
      return `All User Profiles`;
    }
    if (resourceType === InsightsResourceType.Cohorts) {
      return behavior?.value?.cohort?.name;
    }
    if (resourceType === InsightsResourceType.Events && behavior.value.custom) {
      return ``;
    }
  }
  return behavior.value.name;
}

function getMetricEntryBehaviors(behavior: BehaviorMetric['behavior']): MetricEntryBehavior[] {
  if (behavior.type === MetricType.Funnel) {
    return (behavior.behaviors || []).map((behavior) => ({
      id: behavior.id,
      type: behavior.type,
      name: behavior.name,
      filters: behavior.filters?.map((filter) => new FilterClause(filter).toPropertyFilter()),
      filtersDeterminer: behavior.filterDeterminer,
    }));
  }
  if (behavior.type === MetricType.Retention) {
    const behaviors = behavior.behaviors;
    const bornBehavior = behaviors[0];
    const retainedBehavior = behaviors[1];
    return [
      {
        ...(bornBehavior.name?.startsWith(`$custom_event:`) ? {id: parseInt(bornBehavior.name.split(`:`)[1], 10)} : {}),
        type: bornBehavior.name?.startsWith(`$custom_event:`) ? MetricType.CustomEvent : MetricType.Event,
        name: bornBehavior.name,
        filters: bornBehavior.filters?.length
          ? bornBehavior.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter())
          : [],
        filtersDeterminer: bornBehavior.filterDeterminer || `all`,
      },
      {
        ...(retainedBehavior.name?.startsWith(`$custom_event:`)
          ? {id: parseInt(retainedBehavior.name.split(`:`)[1], 10)}
          : {}),
        type: retainedBehavior.name?.startsWith(`$custom_event:`) ? MetricType.CustomEvent : MetricType.Event,
        name: retainedBehavior.name,
        filters: retainedBehavior.filters.map((filterClause) => new FilterClause(filterClause).toPropertyFilter()),
        filtersDeterminer: bornBehavior.filterDeterminer || `all`,
      },
    ];
  }
  // TODO: Handle other behavior types

  return [];
}

/**
 * Transforms event entries to show sections
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getShowSection({
  entries,
  dataGroupId,
}: {
  entries: Array<QueryMetricEntry | MetricEntry>;
  dataGroupId?: string;
}): ShowSection {
  let showSection;

  if (entries.length) {
    const showClauses = entries.map((entry) => {
      if (isFormulaEntry(entry)) {
        return ShowClause.createShowClause({
          formula: {
            definition: entry.event.value,
            name: entry.event.label,
          },
        });
      }

      if (isMetricEntry(entry)) {
        if (entry.behavior.type === MetricType.Funnel) {
          const behavior: BehaviorShowClause[`behavior`] = {
            type: MetricType.Funnel,
            name: null,
            resourceType: null,
            dataGroupId: entry.behavior.dataGroupId,
            dataset: DATASET_MIXPANEL,
            // TODO: remove when we fixed qapi validation
            value: {
              name: `TODO`,
            },
            // TODO: Find a better way to migrate filter -> filters
            filter: [],
            behaviors: entry.behavior.behaviors.map((behavior) => ({
              id: behavior.id,
              type: behavior.type,
              name: behavior.name,
              filters: behavior.filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
              filterDeterminer: behavior.filtersDeterminer,
            })),
            conversionWindowDuration: entry.behavior.conversionWindowDuration,
            conversionWindowUnit: entry.behavior.conversionWindowUnit,
            funnelOrder: entry.behavior.funnelOrder,
            exclusions: entry.behavior.exclusions || [],
            aggregateBy: entry.behavior.aggregateBy,
          };
          const measurement: BehaviorShowClause[`measurement`] = {
            // TODO: change to funnelStepIndex
            stepIndex: entry.measurement.stepIndex,
            math: entry.measurement.aggregationOperator || FUNNELS_MATH_TYPES.CONVERSION_RATE,
            perUserAggregation: null,
            rolling: null,
            cumulative: false,
            property: entry.measurement.property
              ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
              : null,
          };
          return ShowClause.createShowClause({
            behavior,
            measurement,
          });
        }
        if (entry.behavior.type === MetricType.Retention) {
          const behavior: BehaviorShowClause[`behavior`] = {
            type: MetricType.Retention,
            resourceType: null,
            dataGroupId: entry.behavior.dataGroupId,
            dataset: DATASET_MIXPANEL,
            name: null,
            value: null,
            filter: [],
            behaviors: [
              {
                type: entry.behavior.type,
                resourceType: InsightsResourceType.Events,
                name: entry.behavior.behaviors[0].name,
                filters: entry.behavior.behaviors[0].filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
                filterDeterminer: entry.behavior.behaviors[0].filtersDeterminer,
              },
              {
                type: entry.behavior.type,
                resourceType: InsightsResourceType.Events,
                name: entry.behavior.behaviors[1].name,
                filters: entry.behavior.behaviors[1].filters?.map((filter) => FilterClause.fromPropertyFilter(filter)),
                filterDeterminer: entry.behavior.behaviors[1].filtersDeterminer,
              },
            ],
            retentionType: entry.behavior.retentionType,
            retentionAlignmentType: entry.behavior.retentionAlignmentType,
            retentionUnit: entry.behavior.retentionUnit,
            retentionUnbounded: entry.behavior.retentionUnbounded,
            retentionCustomBucketSizes: entry.behavior.retentionCustomBucketSizes,
          };
          const measurement: BehaviorShowClause[`measurement`] = {
            math: entry.measurement.aggregationOperator || RETENTION_MATH_TYPES.UNIQUE,
            perUserAggregation: null,
            rolling: null,
            cumulative: entry.measurement.cumulative || false,
            property: entry.measurement.property
              ? convertPropertySelectiontoBookmarkProperty(entry.measurement.property)
              : null,
            retentionBucketIndex: entry.measurement.retentionBucketIndex,
          };
          return ShowClause.createShowClause({
            behavior,
            measurement,
          });
        }
        return null;
      }
      const {
        event,
        filters,
        property,
        filtersOperator,
        aggregationOperator,
        type,
        aggregationOperatorPerUser,
        rolling,
        cumulative,
      } = entry;
      const filterClauses = filters.map((filter) => FilterClause.fromPropertyFilter(filter));

      let showFilterSection = {};
      if (filterClauses.length) {
        showFilterSection = {
          filter: filterClauses,
          filterDeterminer: DETERMINER_MAPPING[filtersOperator],
        };
      }

      const attrs = {
        behavior: {
          type: MetricType.Event,
          resourceType: null,
          dataGroupId: entry.dataGroupId,
          dataset: DATASET_MIXPANEL,
          value: null,
          filter: [],
          ...showFilterSection,
          ...(isEventEntry(entry) && aggregationOperator === ALL_MATH_TYPES.HISTOGRAM
            ? {customBucket: entry.customBucket}
            : {}),
        },
        measurement: {
          math: aggregationOperator || PRIMITIVE_MATH_TYPES.TOTAL,
          perUserAggregation: (aggregationOperatorPerUser as PER_USER_AGGREGATIONS) || null,
          rolling: rolling || null,
          cumulative: cumulative || false,
          property: null,
          ...(isEventEntry(entry) && entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
        },
      };

      switch (type) {
        case ResourceType.Cohort: {
          attrs.behavior.type = MetricType.Cohort;
          attrs.behavior.resourceType = InsightsResourceType.Cohorts;
          if (isInlineCohortItem(event)) {
            const cohortValue = event.value.cohort;

            attrs.behavior.value = {
              name: event.label,
              resourceType: InsightsResourceType.Cohorts,
              ...cohortValue,
            };
          } else if (isSavedCohortItem(event)) {
            attrs.behavior.value = {
              id: event.value,
              name: event.label,
              resourceType: InsightsResourceType.Cohorts,
              ...(event.renamed ? {renamed: event.renamed} : {}),
            };
          }
          break;
        }
        case ResourceType.User:
        case ResourceType.Event:
          if (type === ResourceType.Event && event.custom) {
            attrs.behavior.type = MetricType.CustomEvent;
          }
          if (type === ResourceType.Event && !event.custom) {
            attrs.behavior.type = MetricType.Event;
          }
          if (type === ResourceType.User) {
            attrs.behavior.type = MetricType.People;
          }
          attrs.behavior.resourceType = QUERY_BUILDER_RESOURCE_TYPE_MAPPING[type];
          attrs.behavior.value = {
            dataset: DATASET_MIXPANEL,
            custom: event.custom,
            resourceType: attrs.behavior.resourceType,
            // Insights uses friendly name for custom event
            name: event.custom ? event.label : event.value,
            ...(event.id ? {id: event.id} : {}),
            ...(event.renamed ? {renamed: event.renamed} : {}),
          };
          break;
      }

      if (property) {
        attrs.measurement.property = convertPropertySelectiontoBookmarkProperty(property);
      }
      return ShowClause.createShowClause(attrs);
    });

    showSection = new ShowSection(showClauses);
  } else {
    showSection = ShowSection.createShowSection({
      behavior: {
        dataGroupId,
        type: MetricType.Event,
        // Cleanup: Missing resourceType
        resourceType: undefined,
      },
    });
  }

  return showSection;
}

export function convertPropertySelectiontoBookmarkProperty(property: PropertySectionItem) {
  const {resourceType, value} = property;
  let customProperty,
    customPropertyId,
    dataGroupId,
    tempDataGroupId,
    joinPropertyType,
    propertyDefaultType,
    propertyObjectKey,
    type,
    unit,
    listItemGroup;

  if (isProperty(property)) {
    ({
      customProperty,
      customPropertyId,
      dataGroupId,
      tempDataGroupId,
      joinPropertyType,
      propertyDefaultType,
      propertyObjectKey,
      type,
      unit,
      listItemGroup,
    } = property);
  }
  return {
    ...(customProperty ? {customProperty} : {}),
    ...(customPropertyId ? {customPropertyId} : {}),
    dataset: DATASET_MIXPANEL,
    ...(dataGroupId ? {dataGroupId} : {}),
    ...(tempDataGroupId ? {tempDataGroupId} : {}),
    ...(joinPropertyType ? {joinPropertyType} : {}),
    ...(listItemGroup ? {listItemGroup} : {}),
    defaultType: propertyDefaultType,
    ...(propertyObjectKey ? {propertyObjectKey} : {}),
    name: value,
    resourceType: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[resourceType],
    type,
    unit: unit ?? null,
  };
}

function generateRetentionBuckets({
  showIndex,
  retentionBucketIndex,
  retentionType,
  retentionUnit,
  dateUnit,
  retentionCustomBucketSizes,
  reportSectionsMetadata,
}: {
  showIndex: number;
  retentionBucketIndex: number;
  retentionType: RetentionType;
  retentionUnit: TimeUnit;
  dateUnit: BirthIntervalUnit;
  retentionCustomBucketSizes: number[];
  reportSectionsMetadata: ReportSectionsMetadata;
}): Array<SectionItem<number>> {
  let items: Array<SectionItem<number>> = [];
  const retentionBucketsSize = reportSectionsMetadata
    ? reportSectionsMetadata.show[showIndex].retention_buckets_size
    : 0;
  if (retentionBucketsSize > 0) {
    items = new Array(retentionBucketsSize).fill(null).map((_, i) => {
      return {
        label: getRetentionBucketName({
          bucketIndex: i,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: i,
        isSelected: i === retentionBucketIndex,
      };
    });
  } else {
    items = [
      {
        label: getRetentionBucketName({
          bucketIndex: 0,
          retentionType,
          retentionUnit,
          dateUnit,
          bucketSizes: retentionCustomBucketSizes,
        }),
        value: 0,
        isSelected: true,
      },
    ];
  }

  return items;
}
